import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface EditTechnique {
  name: string;
  english: string;
  effect: string;
  icon: string;
  color: string;
}

interface ColorParam {
  name: string;
  range: string;
  tip: string;
}

interface Software {
  name: string;
  strength: string;
  platform: string;
  country: string;
}

interface TimelineClip {
  label: string;
  start: number;
  duration: number;
  track: number;
  color: string;
}

interface VideoEditingTemplateProps {
  topic: string;
  subtitle: string;
  editTechniques: EditTechnique[];
  colorParams: ColorParam[];
  software: Software[];
  timelineClips: TimelineClip[];
  postEffects: { label: string; icon: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: VideoEditingTemplateProps = {
  topic: '视频剪辑与后期制作',
  subtitle: 'Video Editing & Post-Production',
  editTechniques: [
    {
      name: '跳切',
      english: 'Jump Cut',
      effect: '加速节奏，制造紧迫感',
      icon: '⚡',
      color: '#06b6d4',
    },
    {
      name: '叠化',
      english: 'Dissolve',
      effect: '时间过渡，情绪渐变',
      icon: '🌊',
      color: '#22d3ee',
    },
    {
      name: '蒙太奇',
      english: 'Montage',
      effect: '压缩时空，意义并列',
      icon: '🎬',
      color: '#67e8f9',
    },
    {
      name: '匹配剪辑',
      english: 'Match Cut',
      effect: '视觉关联，无缝衔接',
      icon: '🔗',
      color: '#a5f3fc',
    },
  ],
  colorParams: [
    { name: '色温', range: '2700K-10000K', tip: '暖色叙事 vs 冷色疏离' },
    { name: '对比度', range: '±100', tip: '电影感高对比 vs 日系低对比' },
    { name: '饱和度', range: '0-200%', tip: '去饱和=高级感，高饱和=活力' },
    { name: 'LUT预设', range: '自定义', tip: 'LOG转Rec.709基础调色' },
  ],
  software: [
    { name: 'Premiere Pro', strength: '通用性最强，行业标准', platform: '全平台', country: '🇺🇸' },
    { name: 'DaVinci Resolve', strength: '调色天花板，免费版够用', platform: '全平台', country: '🇦🇺' },
    { name: 'Final Cut Pro', strength: '磁力时间线，M芯片优化', platform: 'macOS', country: '🇺🇸' },
    { name: '剪映专业版', strength: '智能字幕，模板丰富', platform: '全平台', country: '🇨🇳' },
  ],
  timelineClips: [
    { label: 'V2 · 文字', start: 1, duration: 8, track: 0, color: '#06b6d4' },
    { label: 'V1 · 画面', start: 0, duration: 12, track: 1, color: '#0891b2' },
    { label: 'A1 · 对白', start: 0, duration: 10, track: 2, color: '#22d3ee' },
    { label: 'A2 · 音乐', start: 0, duration: 12, track: 3, color: '#67e8f9' },
    { label: 'A3 · 音效', start: 2, duration: 5, track: 3, color: '#a5f3fc' },
  ],
  postEffects: [
    { label: '关键帧', icon: '🔑' },
    { label: '速度曲线', icon: '📈' },
    { label: '遮罩追踪', icon: '🎭' },
    { label: '降噪稳定', icon: '🔇' },
  ],
  stats: [
    { value: '24/25/30fps', label: '电影/PAL/网络帧率' },
    { value: '15:1', label: '素材与成片平均比' },
    { value: 'H.265', label: '主流编码压缩标准' },
  ],
  bottomQuote:
    '剪辑是电影的第二次创作——它不仅是技术，更是用节奏和画面讲述故事的艺术 —— 沃尔特·默奇',
};

export const VideoEditingTemplate: React.FC<
  Partial<VideoEditingTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const techStart = 30;
  const colorStart = 150;
  const timelineStart = 200;
  const softwareStart = 300;
  const effectStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const timelineDraw = interpolate(
    frame,
    [timelineStart, timelineStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const maxSec = 12;
  const tlBarX = (sec: number) => 110 + (sec / maxSec) * 480;
  const tlBarW = (dur: number) => (dur / maxSec) * 480;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a1a1f 0%, #0c2229 40%, #061215 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating film frame particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 303) % 1920;
        const y = (i * 191) % 1080;
        const size = 3 + (i % 4);
        const drift = Math.sin(frame * 0.025 + i) * 8;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.6);
        const colors = ['#06b6d4', '#22d3ee', '#67e8f9', '#0891b2'];
        const isFrame = i % 5 === 0;
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: isFrame ? size * 2.4 : size,
              height: isFrame ? size * 1.6 : size,
              borderRadius: isFrame ? 2 : '50%',
              background: isFrame ? 'transparent' : colors[i % colors.length],
              border: isFrame
                ? `1px solid ${colors[i % colors.length]}`
                : 'none',
              opacity: pulse,
              boxShadow: isFrame
                ? 'none'
                : `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#0e7490',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#cffafe',
            flexShrink: 0,
          }}
        >
          🎬 后期制作
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#cffafe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Editing Techniques ===== */}
      <div style={{ position: 'absolute', top: 105, left: 36, width: 420 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          剪辑手法
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.editTechniques.map((t, i) => {
            const s = spring({
              frame: frame - (techStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#0a1a1f',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${t.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${t.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 30,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${t.color}30`,
                  }}
                >
                  {t.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: t.color }}
                    >
                      {t.name}
                    </span>
                    <span style={{ fontSize: 22, color: '#64748b' }}>
                      {t.english}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}
                  >
                    {t.effect}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Color Grading */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            调色参数
          </div>
          <svg width="400" height="300" viewBox="0 0 400 300">
            {p.colorParams.map((cp, i) => {
              const s = spring({
                frame: frame - (colorStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              const yPos = i * 72;
              const barColors = ['#ef4444', '#f59e0b', '#06b6d4', '#8b5cf6'];
              return (
                <g key={i} opacity={s}>
                  <text
                    x="5"
                    y={yPos + 22}
                    fill="#cffafe"
                    fontSize="22"
                    fontWeight="700"
                  >
                    {cp.name}
                  </text>
                  <text x="90" y={yPos + 22} fill="#64748b" fontSize="20">
                    {cp.range}
                  </text>
                  <rect
                    x="5"
                    y={yPos + 32}
                    width={320 * s}
                    height="6"
                    rx="3"
                    fill={`${barColors[i]}40`}
                  />
                  <rect
                    x="5"
                    y={yPos + 32}
                    width={220 * s}
                    height="6"
                    rx="3"
                    fill={barColors[i]}
                    opacity={0.8}
                  />
                  <text
                    x="5"
                    y={yPos + 60}
                    fill="#64748b"
                    fontSize="20"
                  >
                    {cp.tip}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* ===== CENTER: Timeline Visualization ===== */}
      <div style={{ position: 'absolute', top: 105, left: 660, width: 600 }}>
        <div
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginBottom: 20,
            letterSpacing: '0.08em',
          }}
        >
          时间线 · 多轨编辑
        </div>
        <svg width="620" height="320" viewBox="0 0 620 320">
          {/* Time ruler */}
          {Array.from({ length: 13 }).map((_, s) => (
            <g key={`ruler-${s}`}>
              <line
                x1={tlBarX(s)}
                y1="15"
                x2={tlBarX(s)}
                y2="270"
                stroke="#164e63"
                strokeWidth="1"
                opacity="0.3"
              />
              <text
                x={tlBarX(s)}
                y="16"
                fill="#64748b"
                fontSize="20"
                textAnchor="middle"
              >
                {s}s
              </text>
            </g>
          ))}

          {/* Playhead */}
          {(() => {
            const playheadPos = interpolate(
              frame,
              [timelineStart, timelineStart + 120],
              [0, 12],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <g>
                <line
                  x1={tlBarX(playheadPos)}
                  y1="15"
                  x2={tlBarX(playheadPos)}
                  y2="270"
                  stroke="#ef4444"
                  strokeWidth="2"
                />
                <polygon
                  points={`${tlBarX(playheadPos) - 6},5 ${tlBarX(playheadPos) + 6},5 ${tlBarX(playheadPos)},14`}
                  fill="#ef4444"
                />
              </g>
            );
          })()}

          {/* Clips */}
          {p.timelineClips.map((clip, i) => {
            const s = spring({
              frame: frame - (timelineStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const yPos = 30 + clip.track * 56;
            return (
              <g key={i} opacity={s}>
                <rect
                  x={tlBarX(clip.start)}
                  y={yPos}
                  width={tlBarW(clip.duration) * timelineDraw}
                  height="46"
                  rx="8"
                  fill={clip.color}
                  opacity={0.7}
                />
                <text
                  x={tlBarX(clip.start) + 10}
                  y={yPos + 29}
                  fill="#0a1a1f"
                  fontSize="18"
                  fontWeight="700"
                >
                  {clip.label}
                </text>
              </g>
            );
          })}

          {/* Track labels */}
          {['V2', 'V1', 'A1', 'A2'].map((label, i) => (
            <text
              key={`tl-${i}`}
              x="80"
              y={53 + i * 56}
              fill="#94a3b8"
              fontSize="17"
              textAnchor="end"
              fontWeight="600"
            >
              {label}
            </text>
          ))}

          {/* Export info */}
          <g
            opacity={interpolate(
              frame,
              [timelineStart + 80, timelineStart + 100],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <text x="30" y="295" fill="#06b6d4" fontSize="20" fontWeight="700">
              渲染输出: H.264/H.265 · 码率 20-50Mbps · 色彩空间 Rec.709
            </text>
          </g>
        </svg>

        {/* Software comparison */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            主流软件
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {p.software.map((sw, i) => {
              const s = spring({
                frame: frame - (softwareStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: '#0a1a1f',
                    borderRadius: 12,
                    padding: '10px 14px',
                    border: '1.5px solid #164e63',
                    opacity: s,
                    transform: `translateX(${(1 - s) * 24}px)`,
                  }}
                >
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{sw.country}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#22d3ee' }}>
                        {sw.name}
                      </span>
                      <span style={{ fontSize: 20, color: '#64748b' }}>
                        {sw.platform}
                      </span>
                    </div>
                    <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 2 }}>
                      {sw.strength}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Post Effects ===== */}
      <div style={{ position: 'absolute', top: 105, right: 36, width: 480 }}>
        <div
          style={{
            fontSize: 30,
            color: '#94a3b8',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          后期技术
        </div>
        {/* Waveform / Scope visualization */}
        <svg width="460" height="220" viewBox="0 0 460 220">
          <text x="5" y="20" fill="#94a3b8" fontSize="20">
            RGB 波形示意
          </text>
          {/* Red channel */}
          {Array.from({ length: 40 }).map((_, i) => {
            const h = 20 + Math.sin(i * 0.5 + 1) * 15 + Math.random() * 5;
            const s = spring({
              frame: frame - (colorStart + 60 + i),
              fps,
              config: { damping: 20, stiffness: 150 },
            });
            return (
              <rect
                key={`r-${i}`}
                x={10 + i * 11}
                y={60 - h * s}
                width="8"
                height={h * s}
                fill="#ef4444"
                opacity={0.4 * s}
                rx="1"
              />
            );
          })}
          {/* Green channel */}
          {Array.from({ length: 40 }).map((_, i) => {
            const h = 18 + Math.cos(i * 0.4 + 2) * 12 + Math.random() * 4;
            const s = spring({
              frame: frame - (colorStart + 80 + i),
              fps,
              config: { damping: 20, stiffness: 150 },
            });
            return (
              <rect
                key={`g-${i}`}
                x={10 + i * 11}
                y={120 - h * s}
                width="8"
                height={h * s}
                fill="#22c55e"
                opacity={0.4 * s}
                rx="1"
              />
            );
          })}
          {/* Blue channel */}
          {Array.from({ length: 40 }).map((_, i) => {
            const h = 22 + Math.sin(i * 0.6 + 3) * 14 + Math.random() * 3;
            const s = spring({
              frame: frame - (colorStart + 100 + i),
              fps,
              config: { damping: 20, stiffness: 150 },
            });
            return (
              <rect
                key={`b-${i}`}
                x={10 + i * 11}
                y={185 - h * s}
                width="8"
                height={h * s}
                fill="#3b82f6"
                opacity={0.4 * s}
                rx="1"
              />
            );
          })}
        </svg>

        {/* Post-production effects */}
        <div style={{ marginTop: 0 }}>
          <div
            style={{
              fontSize: 28,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            后期特效
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.postEffects.map((eff, i) => {
              const s = spring({
                frame: frame - (effectStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a1a1f',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #164e63',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 30, marginBottom: 6 }}>{eff.icon}</div>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#22d3ee' }}
                  >
                    {eff.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Keyboard shortcuts teaser */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 28,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            效率快捷键
          </div>
          {[
            { keys: 'J / K / L', action: '倒放 / 暂停 / 正放' },
            { keys: 'I / O', action: '标记入点 / 出点' },
            { keys: 'Ctrl+Shift+D', action: '默认转场' },
          ].map((shortcut, i) => {
            const s = spring({
              frame: frame - (effectStart + 50 + i * 12),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 6,
                  opacity: s,
                }}
              >
                <span
                  style={{
                    padding: '4px 12px',
                    background: '#164e63',
                    borderRadius: 6,
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#cffafe',
                    fontFamily: 'monospace',
                  }}
                >
                  {shortcut.keys}
                </span>
                <span style={{ fontSize: 22, color: '#94a3b8' }}>
                  {shortcut.action}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 98,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a1a1f',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #164e63',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{ fontSize: 30, fontWeight: 900, color: '#06b6d4' }}
                >
                  {st.value}
                </span>
                <span style={{ fontSize: 20, color: '#94a3b8' }}>
                  {st.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 18,
          left: 36,
          right: 36,
          background: '#0a1a1f',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #0e7490',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🎬</span>
          <span style={{ fontSize: 22, color: '#cffafe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #0e7490, #06b6d4, #67e8f9)',
        }}
      />
    </AbsoluteFill>
  );
};
